import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import { useTheme } from '@mui/material/styles'
import React, { useCallback, useState } from 'react'
import { toast } from 'react-toastify'

import { HHAlert } from 'components/common/HHAlert'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHConfirmDialog from 'components/common/HHConfirmDialog'
import HHDialogActions from 'components/common/HHDialogActions'
import HHDialogTitle from 'components/common/HHDialogTitle'
import ArchiveNoteListItem from 'components/notes/common/ArchiveNoteListItem'
import NoteSkeleton from 'components/notes/common/NoteSkeleton'
import T from 'T'
import { handleError } from 'utils/error'

type NoteRecord = { id: string }

type UnarchiveNoteMutationHook = () => readonly [
  (args: { id: string; noteId: string; data: { archived: boolean } }) => { unwrap: () => Promise<unknown> },
  { isLoading: boolean }
]

type ViewArchiveNotesDialogProps = {
  id: string
  isOpen?: boolean
  isLoading?: boolean
  title?: string
  notes?: NoteRecord[]
  useUnarchiveNoteMutation: UnarchiveNoteMutationHook | (() => unknown)
  onClose: () => void
}

type ArchivedNoteRowProps = {
  note: NoteRecord
  showDivider: boolean
  onUnarchive: (note: NoteRecord) => void
}

const ArchivedNoteRow: React.FC<ArchivedNoteRowProps> = ({ note, showDivider, onUnarchive }) => {
  const handleClick = useCallback(() => {
    onUnarchive(note)
  }, [note, onUnarchive])

  return (
    <React.Fragment key={note.id}>
      <ArchiveNoteListItem note={note} onClick={handleClick} />
      {showDivider && <Divider />}
    </React.Fragment>
  )
}

const ViewArchiveNotesDialog: React.FC<ViewArchiveNotesDialogProps> = ({
  id,
  isOpen = false,
  isLoading = false,
  title = '',
  notes = [],
  useUnarchiveNoteMutation,
  onClose,
}) => {
  const theme = useTheme()
  const [unarchiveNote, { isLoading: isUnarchiveNoteLoading }] = (useUnarchiveNoteMutation as UnarchiveNoteMutationHook)()
  const [isOpenConfirmUnarchiveDialog, setIsOpenConfirmUnarchiveDialog] = useState(false)
  const [selectedNote, setSelectedNote] = useState<NoteRecord | null>(null)

  const handleUnarchiveClick = useCallback((note: NoteRecord) => {
    setSelectedNote(note)
    setIsOpenConfirmUnarchiveDialog(true)
  }, [])

  const handleCloseUnarchiveDialog = useCallback(() => {
    setIsOpenConfirmUnarchiveDialog(false)
  }, [])

  const handleConfirmUnarchived = useCallback(() => {
    if (!selectedNote) return
    unarchiveNote({ id, noteId: selectedNote.id, data: { archived: false } })
      .unwrap()
      .then(() => {
        setSelectedNote(null)
        handleCloseUnarchiveDialog()
        toast.success(T.NOTE_UNARCHIVED_SUCCESSFULLY)
      })
      .catch(handleError)
  }, [handleCloseUnarchiveDialog, id, selectedNote, unarchiveNote])

  return (
    <>
      <HHBaseDialog fullWidth maxWidth="md" open={isOpen} paperPropsSx={{}} onClose={onClose}>
        <HHDialogTitle title={title} onClose={onClose} />
        <DialogContent sx={{ p: 2 }}>
          {isLoading && <NoteSkeleton rows={1} />}
          {!isLoading &&
            (notes.length === 0 ? (
              <HHAlert borderColor={theme.palette.info.light} severity="info">
                {T.NO_ARCHIVED_NOTES_FOUND}
              </HHAlert>
            ) : (
              <List sx={{ py: 0 }}>
                {notes.map((note, index) => (
                  <ArchivedNoteRow key={note.id} note={note} showDivider={notes.length - 1 !== index} onUnarchive={handleUnarchiveClick} />
                ))}
              </List>
            ))}
        </DialogContent>
        <HHDialogActions>
          <Button color="primary" size="small" variant="contained" onClick={onClose}>
            {T.CLOSE}
          </Button>
        </HHDialogActions>
      </HHBaseDialog>
      <HHConfirmDialog
        ConfirmButtonProps={{ loading: isUnarchiveNoteLoading, color: 'primary' }}
        confirmButtonTitle={T.UNARCHIVE}
        confirmDescription={T.UNARCHIVE_NOTE_CONFIRMATION_DESCRIPTION}
        confirmTitle={T.UNARCHIVE_NOTE}
        DialogProps={{ maxWidth: 'xs', fullWidth: true, PaperProps: { sx: { maxWidth: '375px' } } }}
        isOpen={isOpenConfirmUnarchiveDialog}
        onClose={handleCloseUnarchiveDialog}
        onConfirm={handleConfirmUnarchived}
      />
    </>
  )
}

export default ViewArchiveNotesDialog
